import { ViewConfig } from '../../../models/view-config.interface';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { EcmrImportDialogComponent } from '@app/shared/components/ecmr/dialogs/ecmr-import-dialog/ecmr-import-dialog.component';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { AppStore } from '@app/stores/appStore';
import { UserRoles } from 'api/models';
import { SearchStore } from '@app/stores/searchStore';
import { hasEcmr } from '@app/core/stores/permissionsStore';

export interface ConsigneeEcmrConfigDeps {
    router: Router;
    dialog: MatDialog;
    appStore: InstanceType<typeof AppStore>;
    searchStore: InstanceType<typeof SearchStore>;
}

// consignee ecmr view
export class ConsigneeEcmrConfig {
    static create(deps: ConsigneeEcmrConfigDeps): ViewConfig {
        return {
            id: 'consignee-ecmr',
            name: 'eCMR',
            translationKey: 'ECMR.ECMR',
            type: 'ecmr',
            userRole: 'consignee',

            apiConfig: {
                loadData: () => Promise.resolve([]),
            },

            tableConfig: {
                columns: [],
                features: {
                    sortable: false,
                    filterable: false,
                    paginated: false,
                    selectable: false,
                    expandable: false,
                    draggable: false,
                },
            },

            filterConfig: {
                filterKeys: [
                    {
                        keyName: 'ecmrSearch',
                        label: 'ECMR.CONSIGNOR.SEARCH',
                        translationKey: 'ECMR.CONSIGNOR.SEARCH',
                    },
                ],
                showClosedToggle: false,
                enableAdvancedFilters: false,
            },

            searchConfig: {
                validSortFields: [],
                defaultSortField: '',
                defaultSortDirection: 'asc',
            },

            permissions: {
                canView: true,
                canCreate: true,
                canUpdate: true,
                canEdit: false,
                canDelete: false,
            },

            headerActionConfig: {
                headerActions: [
                    {
                        id: 'importEcmr',
                        label: 'Import',
                        translationKey: 'ECMR.CONSIGNOR.IMPORT',
                        icon: 'place_item',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-mr-3 tw-h-14 xl:tw-px-4 xl:!tw-text-xl',
                        position: 'right',
                        order: 1,
                        visible: () => {
                            const currentView =
                                deps.appStore.currentEcmrSubView();
                            const currentSite =
                                deps.appStore.currentOrganizationSite();
                            const isReadOnly =
                                currentSite?.roles?.includes(
                                    UserRoles.RemoteConsignor
                                ) ||
                                currentSite?.roles?.includes(
                                    UserRoles.RemoteCarrier
                                ) ||
                                currentSite?.roles?.includes(
                                    UserRoles.RemoteConsignee
                                );
                            return (
                                hasEcmr() &&
                                currentView === 'overview' &&
                                !isReadOnly
                            );
                        },
                        disabled: () => false,
                        handler: async () => {
                            const dialogRef = deps.dialog.open(
                                EcmrImportDialogComponent,
                                {
                                    width: '75vw',
                                    height: '85vh',
                                    maxHeight: '85vh',
                                    data: {},
                                }
                            );

                            dialogRef.afterClosed().subscribe(result => {
                                if (result) {
                                    deps.searchStore.triggerDataRefresh();
                                }
                            });
                        },
                        tooltip: 'ECMR.CONSIGNOR.IMPORT',
                    },
                    {
                        id: 'createEcmr',
                        label: 'Create',
                        translationKey: 'ECMR.CONSIGNOR.CREATE',
                        icon: 'description',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-mr-3 tw-h-14 xl:tw-px-4 xl:!tw-text-xl',
                        position: 'right',
                        order: 2,
                        visible: () => {
                            const currentView =
                                deps.appStore.currentEcmrSubView();
                            const currentSite =
                                deps.appStore.currentOrganizationSite();
                            const isReadOnly =
                                currentSite?.roles?.includes(
                                    UserRoles.RemoteConsignor
                                ) ||
                                currentSite?.roles?.includes(
                                    UserRoles.RemoteCarrier
                                ) ||
                                currentSite?.roles?.includes(
                                    UserRoles.RemoteConsignee
                                );
                            return (
                                hasEcmr() &&
                                currentView === 'overview' &&
                                !isReadOnly
                            );
                        },
                        disabled: () => false,
                        handler: async () => {
                            deps.router.navigate([
                                RoutingEndpoints.consignee +
                                    '/' +
                                    RoutingEndpoints.ecmr_editor,
                            ]);
                        },
                        tooltip: 'ECMR.CONSIGNOR.CREATE',
                    },
                    {
                        id: 'createTemplate',
                        label: 'Create Template',
                        translationKey: 'ECMR.template_overview.create',
                        icon: 'description',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-mr-3 tw-h-14 xl:tw-px-4 xl:!tw-text-xl',
                        position: 'right',
                        order: 3,
                        visible: () => {
                            const currentView =
                                deps.appStore.currentEcmrSubView();
                            const currentSite =
                                deps.appStore.currentOrganizationSite();
                            const isReadOnly =
                                currentSite?.roles?.includes(
                                    UserRoles.RemoteConsignor
                                ) ||
                                currentSite?.roles?.includes(
                                    UserRoles.RemoteCarrier
                                ) ||
                                currentSite?.roles?.includes(
                                    UserRoles.RemoteConsignee
                                );
                            return (
                                hasEcmr() &&
                                currentView === 'templates' &&
                                !isReadOnly
                            );
                        },
                        disabled: () => false,
                        handler: async () => {
                            deps.router.navigate([
                                RoutingEndpoints.consignee +
                                    '/' +
                                    RoutingEndpoints.ecmr_template_editor,
                            ]);
                        },
                        tooltip: 'ECMR.template_overview.create',
                    },
                ],
            },
        };
    }
}
